import { Component } from '@angular/core';
import { RouterOutlet, RouterLink, Router, NavigationEnd } from '@angular/router';
import { CommonModule } from '@angular/common';
import { filter } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { ToastService } from '../services/toast.service';
import { ConfirmService } from '../services/confirm.service';

@Component({
  selector: 'app-layout',
  standalone: true,
  imports: [CommonModule, RouterOutlet, RouterLink],
  template: `
    <!-- Global Toast Container -->
    <div class="toast-container">
      <div *ngFor="let t of toastService.toasts$ | async" class="toast toast-{{ t.type }}">
        <span class="material-icons toast-icon">
          {{ t.type === 'success' ? 'check_circle' : t.type === 'error' ? 'error' : t.type === 'warning' ? 'warning' : 'info' }}
        </span>
        <span class="toast-message">{{ t.message }}</span>
        <button class="toast-close" (click)="toastService.remove(t.id)" title="Chiudi">
          <span class="material-icons" style="font-size: 16px;">close</span>
        </button>
      </div>
    </div>

    <!-- Global Confirm Modal Backdrop -->
    <div class="modal-backdrop" *ngIf="confirmService.dialog$ | async as dialog">
      <div class="modal-card confirm-modal">
        <div class="confirm-icon-wrapper" [class.danger]="dialog.isDanger">
          <span class="material-icons">{{ dialog.isDanger ? 'warning' : 'help_outline' }}</span>
        </div>
        <h2>{{ dialog.title }}</h2>
        <p>{{ dialog.message }}</p>
        <div class="modal-actions" style="justify-content: center; gap: 12px;">
          <button type="button" class="btn-secondary" (click)="confirmService.handleResult(false)">{{ dialog.cancelText || 'Annulla' }}</button>
          <button type="button" [class]="dialog.isDanger ? 'btn-danger' : 'btn-primary'" (click)="confirmService.handleResult(true)">{{ dialog.confirmText || 'Conferma' }}</button>
        </div>
      </div>
    </div>

    <ng-container *ngIf="!isLoginRoute && (authService.currentUser$ | async) as user; else loginOnly">
      <div class="layout">
        <aside class="sidebar" [class.collapsed]="isCollapsed">
        <div class="brand">
          <div class="brand-logo"><img src="assets/logo_guido.png" alt="Rock&Rullo Logo" /></div>
          <div class="brand-info" *ngIf="!isCollapsed">
            <h2>Rock&Rullo</h2>
            <p>Artigiano & Ufficio</p>
          </div>
          <button class="collapse-toggle-btn" (click)="toggleCollapse()" [title]="isCollapsed ? 'Espandi menu' : 'Comprimi menu'">
            <span class="material-icons">{{ isCollapsed ? 'chevron_right' : 'chevron_left' }}</span>
          </button>
        </div>

        <nav class="nav-links">
          <a routerLink="/" routerLinkActive="active" [routerLinkActiveOptions]="{exact: true}" class="nav-link" [title]="isCollapsed ? 'Dashboard' : ''">
            <span class="material-icons">dashboard</span> <span class="nav-text" *ngIf="!isCollapsed">Dashboard</span>
          </a>
          <a routerLink="/preventivi" routerLinkActive="active" class="nav-link" [title]="isCollapsed ? 'Preventivi' : ''">
            <span class="material-icons">description</span> <span class="nav-text" *ngIf="!isCollapsed">Preventivi</span>
          </a>
          <a routerLink="/fatture" routerLinkActive="active" class="nav-link" [title]="isCollapsed ? 'Fatture' : ''">
            <span class="material-icons">receipt_long</span> <span class="nav-text" *ngIf="!isCollapsed">Fatture</span>
          </a>
          <a routerLink="/appuntamenti" routerLinkActive="active" class="nav-link" [title]="isCollapsed ? 'Appuntamenti' : ''">
            <span class="material-icons">event</span> <span class="nav-text" *ngIf="!isCollapsed">Appuntamenti & Calendario</span>
          </a>
          <a routerLink="/clienti" routerLinkActive="active" class="nav-link" [title]="isCollapsed ? 'Clienti' : ''">
            <span class="material-icons">people</span> <span class="nav-text" *ngIf="!isCollapsed">Clienti</span>
          </a>
          <a routerLink="/impostazioni" routerLinkActive="active" class="nav-link" [title]="isCollapsed ? 'Impostazioni' : ''">
            <span class="material-icons">settings</span> <span class="nav-text" *ngIf="!isCollapsed">Impostazioni</span>
          </a>
          <a routerLink="/help" routerLinkActive="active" class="nav-link" [title]="isCollapsed ? 'Guida' : ''">
            <span class="material-icons">help</span> <span class="nav-text" *ngIf="!isCollapsed">Guida & Manuale</span>
          </a>
        </nav>

        <div class="sidebar-footer">
          <button class="theme-toggle-btn-sidebar" (click)="toggleTheme()" [title]="isDarkTheme ? 'Passa a tema chiaro' : 'Passa a tema scuro'">
            <span class="material-icons">{{ isDarkTheme ? 'light_mode' : 'dark_mode' }}</span>
            <span class="nav-text" *ngIf="!isCollapsed">{{ isDarkTheme ? 'Tema Chiaro' : 'Tema Scuro' }}</span>
          </button>

          <div class="user-card" *ngIf="!isCollapsed">
            <div class="user-avatar">{{ user.nome.charAt(0) }}{{ user.cognome.charAt(0) }}</div>
            <div class="user-details">
              <span class="user-name">{{ user.nome }} {{ user.cognome }}</span>
              <span class="user-role">{{ user.ruolo || 'Professionista' }}</span>
            </div>
          </div>

          <button class="btn-logout" (click)="logout()" [title]="isCollapsed ? 'Disconnetti' : ''">
            <span class="material-icons" style="font-size: 18px;">logout</span> <span class="nav-text" *ngIf="!isCollapsed">Disconnetti</span>
          </button>
        </div>
      </aside>

      <!-- Mobile Floating Pill Bottom Nav -->
      <nav class="mobile-nav">
        <a routerLink="/" routerLinkActive="active" [routerLinkActiveOptions]="{exact: true}" class="mobile-link" (click)="closeMobileMore()">
          <span class="material-icons">dashboard</span><span>Home</span>
        </a>
        <a routerLink="/appuntamenti" routerLinkActive="active" class="mobile-link" (click)="closeMobileMore()">
          <span class="material-icons">event</span><span>Agenda</span>
        </a>
        <a routerLink="/preventivi" routerLinkActive="active" class="mobile-link" (click)="closeMobileMore()">
          <span class="material-icons">description</span><span>Preventivi</span>
        </a>
        <a routerLink="/clienti" routerLinkActive="active" class="mobile-link" (click)="closeMobileMore()">
          <span class="material-icons">people</span><span>Clienti</span>
        </a>
        <button class="mobile-link mobile-more-btn" (click)="toggleMobileMore()" style="background: none; border: none; cursor: pointer;">
          <span class="material-icons">more_horiz</span><span>Altro</span>
        </button>
      </nav>

      <!-- Mobile More Menu Bottom Sheet -->
      <div class="mobile-more-sheet-backdrop" *ngIf="showMobileMore" (click)="closeMobileMore()">
        <div class="mobile-more-sheet" (click)="$event.stopPropagation()">
          <div class="sheet-handle"></div>
          <h3>Altre Sezioni & Opzioni</h3>
          <div class="sheet-links">
            <a routerLink="/fatture" class="sheet-link" (click)="closeMobileMore()">
              <span class="material-icons">receipt_long</span>
              <div>
                <strong>Fatture & Contabilità</strong>
                <span>Gestisci fatture emesse e bozze</span>
              </div>
            </a>
            <a routerLink="/impostazioni" class="sheet-link" (click)="closeMobileMore()">
              <span class="material-icons">settings</span>
              <div>
                <strong>Impostazioni & Profilo</strong>
                <span>Dati fiscali, P.IVA e IBAN per PDF</span>
              </div>
            </a>
            <a routerLink="/help" class="sheet-link" (click)="closeMobileMore()">
              <span class="material-icons">help</span>
              <div>
                <strong>Guida & Manuale d'Uso</strong>
                <span>Come usare l'app e il flusso di lavoro</span>
              </div>
            </a>
            <button class="sheet-link sheet-btn" (click)="toggleTheme(); closeMobileMore()">
              <span class="material-icons">{{ isDarkTheme ? 'light_mode' : 'dark_mode' }}</span>
              <div>
                <strong>{{ isDarkTheme ? 'Tema Chiaro' : 'Tema Scuro' }}</strong>
                <span>Cambia aspetto visivo</span>
              </div>
            </button>
            <button class="sheet-link sheet-btn text-danger" (click)="logout(); closeMobileMore()">
              <span class="material-icons">logout</span>
              <div>
                <strong>Disconnetti</strong>
                <span>Esci dall'account</span>
              </div>
            </button>
          </div>
        </div>
      </div>

      <main class="main-content">
        <router-outlet></router-outlet>
      </main>
      </div>
    </ng-container>
    <ng-template #loginOnly>
      <router-outlet></router-outlet>
    </ng-template>
  `,
  styles: [`
    .layout { display: flex; height: 100vh; overflow: hidden; background: var(--bg-main); color: var(--text-main); }
    .sidebar { width: 280px; height: 100vh; position: sticky; top: 0; background: var(--surface); border-right: 1px solid var(--border); display: flex; flex-direction: column; padding: 24px 16px; box-shadow: 4px 0 24px rgba(0,0,0,0.03); z-index: 10; transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1), padding 0.3s cubic-bezier(0.4, 0, 0.2, 1); flex-shrink: 0; box-sizing: border-box; overflow-y: auto; }
    .sidebar.collapsed { width: 84px; padding: 24px 10px; }
    
    .brand { display: flex; align-items: center; gap: 12px; padding: 0 4px 24px 4px; border-bottom: 1px solid var(--border); margin-bottom: 20px; }
    .sidebar:not(.collapsed) .brand { justify-content: space-between; }
    .sidebar.collapsed .brand { flex-direction: column; align-items: center; gap: 12px; padding: 0 0 20px 0; }
    
    .brand-logo { width: 44px; height: 44px; background: var(--surface); border-radius: 14px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; box-shadow: 0 4px 12px rgba(200, 90, 50, 0.2); border: 1px solid var(--border); overflow: hidden; }
    .brand-logo img { width: 100%; height: 100%; object-fit: cover; }
    .brand-info { overflow: hidden; flex: 1; }
    .brand h2 { margin: 0; font-size: 17px; font-weight: 700; color: var(--text-main); letter-spacing: -0.3px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .brand p { margin: 2px 0 0; font-size: 11px; color: var(--text-muted); font-weight: 500; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    
    .collapse-toggle-btn { background: none; border: 1px solid var(--border); width: 32px; height: 32px; border-radius: 50%; display: flex; align-items: center; justify-content: center; cursor: pointer; color: var(--text-muted); transition: all 0.2s; flex-shrink: 0; }
    .collapse-toggle-btn:hover { background: var(--primary-light); color: var(--primary); border-color: var(--primary); }
    .collapse-toggle-btn .material-icons { font-size: 18px; }
    .sidebar:not(.collapsed) .collapse-toggle-btn { margin-left: auto; }
    .sidebar.collapsed .collapse-toggle-btn { width: 28px; height: 28px; }
    .sidebar.collapsed .collapse-toggle-btn .material-icons { font-size: 16px; }

    .nav-links { flex: 1; display: flex; flex-direction: column; gap: 8px; }
    .nav-link { display: flex; align-items: center; gap: 14px; color: var(--text-muted); text-decoration: none; padding: 12px 16px; border-radius: 12px; transition: all 0.2s ease; font-weight: 500; font-size: 14px; white-space: nowrap; overflow: hidden; }
    .sidebar.collapsed .nav-link { justify-content: center; padding: 12px; }
    .nav-link .material-icons { font-size: 20px; transition: color 0.2s; flex-shrink: 0; }
    .nav-link:hover { background: var(--primary-light); color: var(--primary); transform: translateX(2px); }
    .nav-link.active { background: linear-gradient(135deg, var(--primary), var(--primary-dark)); color: #ffffff; box-shadow: 0 6px 16px rgba(200, 90, 50, 0.35); }
    .nav-link.active .material-icons { color: #ffffff; }

    .sidebar-footer { border-top: 1px solid var(--border); padding-top: 20px; display: flex; flex-direction: column; gap: 12px; }
    
    .theme-toggle-btn-sidebar { display: flex; align-items: center; gap: 12px; background: none; border: 1px solid var(--border); width: 100%; padding: 11px 16px; border-radius: 12px; cursor: pointer; color: var(--text-muted); transition: all 0.2s; font-weight: 500; font-size: 14px; box-sizing: border-box; }
    .sidebar.collapsed .theme-toggle-btn-sidebar { justify-content: center; padding: 11px; }
    .theme-toggle-btn-sidebar:hover { background: var(--primary-light); color: var(--primary); border-color: var(--primary); }
    .theme-toggle-btn-sidebar .material-icons { font-size: 18px; flex-shrink: 0; }

    .user-card { display: flex; align-items: center; gap: 12px; padding: 10px 14px; background: var(--bg-main); border-radius: 12px; border: 1px solid var(--border); }
    .user-avatar { width: 36px; height: 36px; background: linear-gradient(135deg, #f59e0b, #d97706); color: white; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-weight: 600; font-size: 13px; flex-shrink: 0; box-shadow: 0 2px 8px rgba(245, 158, 11, 0.3); }
    .user-details { display: flex; flex-direction: column; overflow: hidden; }
    .user-name { font-size: 13px; font-weight: 600; color: var(--text-main); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .user-role { font-size: 11px; color: var(--text-muted); text-transform: capitalize; }
    
    .btn-logout { display: flex; align-items: center; justify-content: center; gap: 8px; background: rgba(239, 68, 68, 0.08); color: var(--danger); border: 1px solid rgba(239, 68, 68, 0.2); padding: 11px; border-radius: 12px; cursor: pointer; font-weight: 600; font-size: 13px; transition: all 0.2s; width: 100%; box-sizing: border-box; }
    .sidebar.collapsed .btn-logout { padding: 11px; }
    .btn-logout:hover { background: var(--danger); color: white; border-color: var(--danger); box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3); }

    .mobile-nav { display: none; }
    .main-content { flex: 1; height: 100vh; padding: 40px; overflow-y: auto; box-sizing: border-box; }

    /* Toast Container & Toasts */
    .toast-container {
      position: fixed;
      top: 24px;
      right: 24px;
      z-index: 99999;
      display: flex;
      flex-direction: column;
      gap: 12px;
      max-width: 400px;
      width: 100%;
      box-sizing: border-box;
      pointer-events: none;
    }
    .toast {
      pointer-events: auto;
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 14px 18px;
      border-radius: 12px;
      background: var(--surface);
      color: var(--text-main);
      box-shadow: var(--shadow-lg);
      border: 1px solid var(--border);
      animation: toastSlideIn 0.3s cubic-bezier(0.16, 1, 0.3, 1);
    }
    @keyframes toastSlideIn {
      from { opacity: 0; transform: translateY(-16px) scale(0.95); }
      to { opacity: 1; transform: translateY(0) scale(1); }
    }
    .toast-success { border-left: 4px solid var(--success); }
    .toast-success .toast-icon { color: var(--success); }
    .toast-error { border-left: 4px solid var(--danger); }
    .toast-error .toast-icon { color: var(--danger); }
    .toast-warning { border-left: 4px solid var(--warning); }
    .toast-warning .toast-icon { color: var(--warning); }
    .toast-info { border-left: 4px solid var(--primary); }
    .toast-info .toast-icon { color: var(--primary); }
    
    .toast-message { flex: 1; font-size: 14px; font-weight: 500; line-height: 1.4; }
    .toast-close { background: none; border: none; color: var(--text-muted); cursor: pointer; display: flex; align-items: center; justify-content: center; padding: 4px; border-radius: 4px; transition: background 0.2s; }
    .toast-close:hover { background: var(--bg-main); color: var(--text-main); }

    /* Modal Backdrop & Confirm Modal */
    .modal-backdrop { 
      position: fixed; 
      top: 0; 
      left: 0; 
      right: 0;
      bottom: 0;
      width: 100vw; 
      height: 100vh; 
      box-sizing: border-box;
      background: rgba(15, 23, 42, 0.65); 
      display: flex; 
      justify-content: center; 
      align-items: center; 
      z-index: 9999; 
    }
    .modal-card { 
      background: var(--surface); 
      padding: 32px; 
      border-radius: 16px; 
      width: 100%; 
      max-width: 560px; 
      box-shadow: var(--shadow-lg); 
      border: 1px solid var(--border); 
      box-sizing: border-box;
    }
    .confirm-modal {
      text-align: center;
      max-width: 440px !important;
      padding: 36px 28px !important;
    }
    .confirm-icon-wrapper {
      width: 56px;
      height: 56px;
      border-radius: 50%;
      background: var(--primary-light);
      color: var(--primary);
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 0 auto 20px auto;
    }
    .confirm-icon-wrapper.danger {
      background: rgba(239, 68, 68, 0.12);
      color: var(--danger);
    }
    .confirm-icon-wrapper .material-icons {
      font-size: 28px;
    }
    .confirm-modal h2 {
      margin-top: 0;
      margin-bottom: 10px;
      font-size: 20px;
      color: var(--text-main);
    }
    .confirm-modal p {
      color: var(--text-muted);
      font-size: 14px;
      margin-bottom: 28px;
      line-height: 1.5;
    }
    .btn-danger {
      background: var(--danger);
      color: white;
      border: none;
      padding: 11px 20px;
      border-radius: 8px;
      font-weight: 600;
      cursor: pointer;
      transition: background 0.2s;
    }
    .btn-danger:hover {
      opacity: 0.9;
    }

    /* Mobile More Sheet */
    .mobile-more-sheet-backdrop {
      position: fixed;
      top: 0; left: 0; right: 0; bottom: 0;
      background: rgba(15, 23, 42, 0.65);
      z-index: 10000;
      display: flex;
      align-items: flex-end;
      animation: fadeIn 0.2s ease;
    }
    .mobile-more-sheet {
      background: var(--surface);
      width: 100%;
      border-top-left-radius: 24px;
      border-top-right-radius: 24px;
      padding: 20px 24px 100px 24px;
      box-shadow: var(--shadow-lg);
      border-top: 1px solid var(--border);
      box-sizing: border-box;
      animation: slideUpSheet 0.3s cubic-bezier(0.16, 1, 0.3, 1);
    }
    @keyframes slideUpSheet {
      from { transform: translateY(100%); }
      to { transform: translateY(0); }
    }
    .sheet-handle {
      width: 40px;
      height: 4px;
      background: var(--border);
      border-radius: 2px;
      margin: 0 auto 16px auto;
    }
    .mobile-more-sheet h3 {
      margin: 0 0 16px 0;
      font-size: 16px;
      font-weight: 700;
      color: var(--text-main);
    }
    .sheet-links {
      display: flex;
      flex-direction: column;
      gap: 10px;
    }
    .sheet-link {
      display: flex;
      align-items: center;
      gap: 16px;
      padding: 14px 16px;
      background: var(--bg-main);
      border: 1px solid var(--border);
      border-radius: 14px;
      text-decoration: none;
      color: var(--text-main);
      transition: background 0.2s;
      cursor: pointer;
      width: 100%;
      box-sizing: border-box;
      text-align: left;
    }
    .sheet-link:hover {
      background: var(--surface-hover);
      border-color: var(--primary);
    }
    .sheet-link .material-icons {
      font-size: 24px;
      color: var(--primary);
    }
    .sheet-link div {
      display: flex;
      flex-direction: column;
    }
    .sheet-link strong {
      font-size: 14px;
      font-weight: 700;
    }
    .sheet-link span {
      font-size: 11px;
      color: var(--text-muted);
    }
    .sheet-btn {
      border: 1px solid var(--border);
      font-family: inherit;
    }
    .text-danger .material-icons { color: var(--danger) !important; }
    .text-danger strong { color: var(--danger); }

    @media (max-width: 768px) {
      .layout { flex-direction: column; }
      .sidebar { display: none; }
      .mobile-nav { 
        display: flex; 
        position: fixed; 
        bottom: 20px; 
        left: 20px; 
        right: 20px; 
        background: var(--surface); 
        border: 1px solid var(--border); 
        border-radius: 32px; 
        padding: 10px 16px; 
        justify-content: space-around; 
        align-items: center;
        z-index: 1000; 
        box-shadow: 0 16px 36px rgba(0, 0, 0, 0.16); 
        backdrop-filter: blur(12px); 
      }
      .mobile-link { 
        display: flex; 
        flex-direction: column; 
        align-items: center; 
        gap: 2px; 
        text-decoration: none; 
        color: var(--text-muted); 
        font-size: 10px; 
        font-weight: 500; 
        transition: all 0.2s; 
        padding: 6px 10px;
        border-radius: 16px;
      }
      .mobile-link .material-icons { font-size: 22px; }
      .mobile-link.active { 
        color: var(--primary); 
        background: var(--primary-light);
        font-weight: 600; 
      }
      .main-content { padding: 20px 20px 110px 20px; max-height: none; }
    }
  `],
})
export class AppLayoutComponent {
  isDarkTheme = false;
  isCollapsed = false;
  showMobileMore = false;
  isLoginRoute = false;

  constructor(
    public authService: AuthService, 
    public router: Router,
    public toastService: ToastService,
    public confirmService: ConfirmService
  ) {
    this.isLoginRoute = this.router.url.startsWith('/login');
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: any) => {
      this.isLoginRoute = event.urlAfterRedirects.startsWith('/login') || event.url.startsWith('/login');
    });
    const savedTheme = localStorage.getItem('guidapp_theme');
    if (savedTheme === 'dark') {
      this.isDarkTheme = true;
      document.body.classList.add('dark-theme');
    }
    const savedCollapse = localStorage.getItem('guidapp_sidebar_collapsed');
    if (savedCollapse === 'true') {
      this.isCollapsed = true;
    }
  }

  get isLoginRouteGetter(): boolean {
    return this.router.url.startsWith('/login');
  }

  toggleTheme() {
    this.isDarkTheme = !this.isDarkTheme;
    if (this.isDarkTheme) {
      document.body.classList.add('dark-theme');
      localStorage.setItem('guidapp_theme', 'dark');
    } else {
      document.body.classList.remove('dark-theme');
      localStorage.setItem('guidapp_theme', 'light');
    }
  }

  toggleCollapse() {
    this.isCollapsed = !this.isCollapsed;
    localStorage.setItem('guidapp_sidebar_collapsed', String(this.isCollapsed));
  }

  toggleMobileMore() {
    this.showMobileMore = !this.showMobileMore;
  }

  closeMobileMore() {
    this.showMobileMore = false;
  }

  logout() {
    this.authService.logout();
    this.router.navigate(['/login']);
  }
}
